"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type FC, type ReactNode } from "react";
import { APIProvider, ColorScheme, Map, RenderingType, useApiIsLoaded } from "@vis.gl/react-google-maps";
import type { ListPractitionersQuery } from "cmw-client";
import { PractitionerCard } from "@/components/practitioner-card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/spinner";
import { useInfinitePublicPractitioners, type UseInfinitePublicPractitionersResult } from "@/hooks/use-public-practitioners";
import { cn } from "@/lib/cn";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
  AUSTRALIA_CENTER,
  AUSTRALIA_ZOOM,
  CHROMELESS_MAP_OPTIONS,
  coordinatesOf,
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_MAP_ID,
  HOVER_DEBOUNCE_MS,
  LOAD_MORE_MARGIN,
  LOCATION_ZOOM,
  MAP_LIBRARIES,
  MIN_LOADING_MS,
  MOBILE_CARD_CLEARANCE_Y,
  MOBILE_LOCATION_ZOOM,
  PAGE_SIZE,
  type Coordinates,
} from "../../lib/map-options";
import { MapCamera } from "./map-camera";
import { MapCardCarousel } from "./map-card-carousel";
import { PractitionerMarkers, type PinnedPractitioner } from "./practitioner-markers";
import { SlidePanelToggle } from "./slide-panel-toggle";

type PractitionerMapsViewProps = {
  query?: ListPractitionersQuery;
  center?: Coordinates | null;
  className?: string;
  toolbar?: ReactNode;
};

type MapsViewInnerProps = {
  center: Coordinates | null;
} & Pick<UseInfinitePublicPractitionersResult, "practitioners" | "hasMore" | "loadMore" | "isLoadingMore">;

const MapsViewInner: FC<MapsViewInnerProps> = ({ center, practitioners, hasMore, loadMore, isLoadingMore }) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const rowsRef = useRef<Record<string, HTMLDivElement | null>>({});
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* Tailwind's `lg` breakpoint */
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const [isOpen, setIsOpen] = useState(true);
  const [hovered, setHovered] = useState<PinnedPractitioner | null>(null);
  const [selected, setSelected] = useState<PinnedPractitioner | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const selectPin = useCallback((pin: PinnedPractitioner) => {
    setSelected(pin);
    setExpandedId(null);
    /* Touch fires mouseenter with no mouseleave, which would pin the marker highlight */
    setHovered(null);
  }, []);

  const clearSelection = useCallback(() => {
    setSelected(null);
    setExpandedId(null);
  }, []);

  const toggleExpanded = useCallback((pin: PinnedPractitioner) => {
    setExpandedId((current) => (current === pin.id ? null : pin.id));
  }, []);

  /* Mobile's carousel keeps its selection; only the desktop InfoWindow is dismissible */
  const canDismiss = isDesktop && selected !== null;

  useEffect(() => {
    if (!canDismiss) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) clearSelection();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canDismiss, clearSelection]);

  const pinned = useMemo(
    () =>
      practitioners.flatMap((practitioner, index) => {
        const coordinates = coordinatesOf(practitioner);
        return coordinates ? [{ id: `${practitioner.username}-${index}`, practitioner, coordinates }] : [];
      }),
    [practitioners],
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      { rootMargin: LOAD_MORE_MARGIN },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, loadMore, pinned.length]);

  useEffect(() => {
    if (!selected) return;

    rowsRef.current[selected.id]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selected]);

  const setRowRef = useCallback((row: HTMLDivElement | null, id: string) => {
    if (row) rowsRef.current[id] = row;
    else delete rowsRef.current[id];
  }, []);

  const hoverRow = useCallback((pin: PinnedPractitioner | null) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);

    if (!pin) {
      setHovered(null);
      return;
    }

    hoverTimeoutRef.current = setTimeout(() => setHovered(pin), HOVER_DEBOUNCE_MS);
  }, []);

  useEffect(
    () => () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    },
    [],
  );

  const activeId = (hovered ?? selected)?.id;
  const locationZoom = isDesktop ? LOCATION_ZOOM : MOBILE_LOCATION_ZOOM;

  return (
    <div className="flex size-full flex-col lg:flex-row">
      <div
        className={cn(
          /* Mobile gets the map alone; the stacked list left only a strip of map */
          "relative hidden w-full shrink-0 transition-all duration-300 ease-in-out lg:block",
          isOpen ? "h-4/5 lg:h-auto lg:w-436" : "h-0 lg:h-auto lg:w-0",
        )}
      >
        <aside className="h-full w-full overflow-hidden bg-background-grey">
          <ScrollArea className="h-full w-full lg:w-436" viewportClassName="p-16">
            <div className="flex flex-col gap-8">
              {pinned.map((pin) => (
                <div
                  key={pin.id}
                  ref={(row) => setRowRef(row, pin.id)}
                  role="button"
                  tabIndex={0}
                  onClick={() => selectPin(pin)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") selectPin(pin);
                  }}
                  onMouseEnter={() => hoverRow(pin)}
                  onMouseLeave={() => hoverRow(null)}
                  className="cursor-pointer"
                >
                  <PractitionerCard
                    source={pin.practitioner}
                    variant="map-side-bar"
                    className={cn(
                      "w-full border transition-colors",
                      activeId === pin.id ? "border-brand-blue" : "border-transparent hover:border-neutral-light-grey",
                    )}
                  />
                </div>
              ))}

              {pinned.length === 0 && (
                <p className="px-4 py-16 text-14 text-neutral-black/50">
                  No practitioners with a mapped address yet.
                </p>
              )}

              <div ref={sentinelRef} aria-hidden className="h-1" />
              {isLoadingMore && <p className="px-4 py-8 text-14 text-neutral-black/50">Loading more…</p>}
            </div>
          </ScrollArea>
        </aside>

        <SlidePanelToggle isOpen={isOpen} onToggle={() => setIsOpen((open) => !open)} />
      </div>

      <div className="relative min-h-0 flex-1">
        <Map
          mapId={GOOGLE_MAPS_MAP_ID}
          defaultCenter={center ?? AUSTRALIA_CENTER}
          defaultZoom={center ? locationZoom : AUSTRALIA_ZOOM}
          renderingType={RenderingType.VECTOR}
          colorScheme={ColorScheme.LIGHT}
          reuseMaps
          onClick={canDismiss ? clearSelection : undefined}
          className="size-full"
          {...CHROMELESS_MAP_OPTIONS}
        >
          <MapCamera
            pinned={pinned}
            center={center}
            selected={selected}
            locationZoom={locationZoom}
            cardClearanceY={isDesktop ? undefined : MOBILE_CARD_CLEARANCE_Y}
          />
          <PractitionerMarkers
            pinned={pinned}
            /* Mobile replaces the InfoWindow with the bottom carousel */
            shown={isDesktop ? (hovered ?? selected) : null}
            activeId={activeId}
            onHover={setHovered}
            onSelect={selectPin}
            onDismiss={canDismiss ? clearSelection : undefined}
          />
        </Map>

        <MapCardCarousel
          pinned={pinned}
          active={selected}
          expandedId={expandedId}
          onSelect={selectPin}
          onToggleExpand={toggleExpanded}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          loadMore={loadMore}
        />
      </div>
    </div>
  );
};

const MapsViewLoader: FC<{ query: ListPractitionersQuery; center: Coordinates | null; toolbar?: ReactNode }> = ({
  query,
  center,
  toolbar,
}) => {
  const isApiLoaded = useApiIsLoaded();
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const { practitioners, hasMore, loadMore, isLoadingMore, isLoading } = useInfinitePublicPractitioners(
    query,
    PAGE_SIZE,
  );

  useEffect(() => {
    const timeout = setTimeout(() => setMinTimeElapsed(true), MIN_LOADING_MS);
    return () => clearTimeout(timeout);
  }, []);

  if (!isApiLoaded || isLoading || !minTimeElapsed) {
    return (
      <div className="flex size-full items-center justify-center">
        <Spinner size={40} className="text-brand-blue" />
      </div>
    );
  }

  return (
    <>
      <MapsViewInner
        center={center}
        practitioners={practitioners}
        hasMore={hasMore}
        loadMore={loadMore}
        isLoadingMore={isLoadingMore}
      />
      {toolbar && <div className="absolute top-16 right-16 z-20 flex items-center gap-8">{toolbar}</div>}
    </>
  );
};

export const PractitionerMapsView: FC<PractitionerMapsViewProps> = ({
  query = {},
  center = null,
  className,
  toolbar,
}) => (
  <div className={cn("h-dvh snap-start", className)}>
    <div className="relative size-full overflow-hidden">
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={MAP_LIBRARIES}>
        <MapsViewLoader query={query} center={center} toolbar={toolbar} />
      </APIProvider>
    </div>
  </div>
);
